import { blobToDataUrl } from "@/lib/clipboard/image-clipboard";

/**
 * Client-side image downscaling for anything that gets sent as base64 inside a JSON body.
 *
 * This is a TRANSPORT downscale, not the authority on output quality: the server re-encodes every
 * image it stores (app/avatar_processing.go). It exists so a 12 MB phone photo never enters a
 * request body in the first place. That is also why the default quality here (0.82) is higher than
 * the server's (72) — compressing hard twice is visibly worse than compressing once.
 */

/** Reject before decoding: a 50 MP source would allocate hundreds of MB of canvas in the renderer. */
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

/**
 * Scale factor that fits w x h inside maxDim on the long edge. Never upscales.
 * @returns {{width: number, height: number}}
 */
export function fitWithin(width, height, maxDim) {
  const w = Math.max(0, Math.floor(width));
  const h = Math.max(0, Math.floor(height));
  if (!w || !h || !Number.isFinite(maxDim) || maxDim <= 0) return { width: w, height: h };
  const longest = Math.max(w, h);
  if (longest <= maxDim) return { width: w, height: h };
  const scale = maxDim / longest;
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

/** Approximate decoded byte length of a data URI's payload (base64 is 4 chars per 3 bytes). */
export function dataUrlByteLength(dataUrl) {
  const value = String(dataUrl || "");
  const comma = value.indexOf(",");
  if (comma < 0) return 0;
  const payload = value.slice(comma + 1);
  if (!payload) return 0;
  const padding = payload.endsWith("==") ? 2 : payload.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((payload.length * 3) / 4) - padding);
}

/**
 * Decode, downscale and re-encode an image file to a data URI.
 *
 * @param {File|Blob} fileOrBlob
 * @param {{maxDim?: number, quality?: number, mimeType?: string}} [options]
 * @returns {Promise<{dataUrl: string, mimeType: string, width: number, height: number, bytes: number}>}
 * @throws {Error} with a user-facing message — callers surface it as a toast.
 */
export async function resizeImageToDataUrl(fileOrBlob, options = {}) {
  const { maxDim = 1024, quality = 0.82, mimeType = "image/jpeg" } = options;
  if (!fileOrBlob) {
    throw new Error("No image selected.");
  }
  if (fileOrBlob.size > MAX_SOURCE_BYTES) {
    throw new Error("That image is too large — pick one under 25 MB.");
  }

  let bitmap;
  try {
    // imageOrientation matters: canvas discards EXIF, so a rotated phone photo drawn without this
    // is stored sideways and nothing downstream can recover the original orientation.
    bitmap = await createImageBitmap(fileOrBlob, { imageOrientation: "from-image" });
  } catch {
    // HEIC lands here — Chromium has no decoder for it, and neither does the server.
    throw new Error("That image format isn't supported — save it as JPEG or PNG first.");
  }

  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height, maxDim);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      throw new Error("Could not process that image.");
    }
    // Drawn onto a transparent canvas with no backing fill: a cut-out portrait has to KEEP its
    // transparency, because it sits over a gradient in the app. Flattening it here would be
    // invisible until the photo reached the phone.
    ctx.drawImage(bitmap, 0, 0, width, height);

    // Format follows the content, not the caller: PNG only when the image actually has
    // transparency (it is several times larger), JPEG otherwise.
    const outputType = hasTransparency(ctx, width, height) ? "image/png" : mimeType;
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (next) => (next ? resolve(next) : reject(new Error("Could not process that image."))),
        outputType,
        quality,
      );
    });
    const dataUrl = await blobToDataUrl(blob);
    return {
      dataUrl,
      mimeType: outputType,
      width,
      height,
      bytes: dataUrlByteLength(dataUrl),
    };
  } finally {
    bitmap.close?.();
  }
}

/**
 * Whether any pixel on the canvas is less than fully opaque.
 *
 * A full scan rather than a sample: a cut-out portrait can be opaque across most of its area and
 * transparent only at the edges, and missing that would flatten exactly the images this exists for.
 */
function hasTransparency(ctx, width, height) {
  if (!width || !height) return false;
  let data;
  try {
    ({ data } = ctx.getImageData(0, 0, width, height));
  } catch {
    // A tainted canvas cannot be read; assume opaque and keep the smaller encoding.
    return false;
  }
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
}
